// Extracted from original/src/App.tsx (ADR-08 §2.1 "copy, then cut logic"):
//   CapitalizeProfitModal  :45040-45160  (رسملة الأرباح)
//   WithdrawProfitModal    :45164-45284  (سحب الأرباح يدوياً)
//   ConvertProfitModal     :45288-45432  (تحويل العمولات والأرباح إلى دينار)
//   AddExpenseModal        :47399-47510  (إضافة صرفية جديدة)
// Kept: every class string, header colour, icon and size, the backdrop and the motion frames, the field order, the
// "متوفر" figures in the currency options, the explanation boxes, the submit buttons and their labels.
// Cut: `dashboardData.pendingProfit` / `pendingProfitUSD` / `availableUSDProfits` → `pending` labels from the server
// (`GET /profits`, pendingProfit, D-099); `handleCapitalizeProfit` / `handleWithdrawProfit` / `handleConvertUSDProfits`
// / `handleAddExpense` → `onSubmit` (the container posts through the gateway: preview → PIN → commit, D-232);
// the conversion's browser arithmetic and unit guess (`rawRate >= 10000 ? rawRate / 100`, D06 R-16) → the server
// preview's IQD figure (`equivalent`, D-101, D-012); `users` → `employees` from the server.
// Changed by decision: the currency choices are IQD and USD only (other currencies' profits are IQD, D-067 / D-085;
// USD hidden with D-066); the conversion's currency select keeps only the USD option (D-101) and its rate example
// shows the D-012 quote unit (V-46); the expense form keeps the old fields (no box field: the main vault, or the
// recorder's own sub-safe when sub-safes are on, D-102).
// Added: data-testid attributes.
import { AnimatePresence, motion } from 'motion/react';
import { Plus, RefreshCw, TrendingDown, TrendingUp, X } from 'lucide-react';
import type { FormEvent } from 'react';
import { CustomSelect } from '../components/CustomSelect.js';
import { NumericInput } from '../components/NumericInput.js';
import { useI18n } from '../i18n-context.js';

export interface ProfitSettlementValues {
  readonly currency: 'IQD' | 'USD';
  readonly amount: string;
  readonly note: string;
}

interface SettlementModalProps {
  readonly open: boolean;
  readonly values: ProfitSettlementValues;
  readonly onChange: (v: ProfitSettlementValues) => void;
  /** Pending profit labels per currency ("40,000"); USD absent when USD dealing is hidden (D-066). */
  readonly pending: { readonly IQD: string; readonly USD: string | null };
  readonly busy: boolean;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
}

const submit = (fn: () => void) => (e: FormEvent) => {
  e.preventDefault();
  fn();
};

export function CapitalizeProfitModal(p: SettlementModalProps) {
  const { t } = useI18n();
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="capitalize-modal">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={p.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border"
          >
            <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-emerald-900 text-white">
              <h3 className="text-xl font-black flex items-center gap-2 uppercase tracking-tight">
                <TrendingUp className="text-emerald-400" />
                {t('pf.cap_title')}
              </h3>
              <button onClick={p.onClose} className="text-emerald-400 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={submit(p.onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('pf.select_currency')}</label>
                <select
                  value={p.values.currency || 'IQD'}
                  onChange={(e) => p.onChange({ ...p.values, currency: e.target.value as 'IQD' | 'USD' })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-sm font-black dark:text-[#f0f6fc] transition-all"
                  data-testid="capitalize-currency"
                >
                  <option value="IQD">{t('pf.opt_iqd_available', { amount: p.pending.IQD })}</option>
                  {p.pending.USD !== null && <option value="USD">{t('pf.opt_usd_available', { amount: p.pending.USD })}</option>}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('pf.cap_amount', { cur: p.values.currency || 'IQD' })}</label>
                <NumericInput
                  required
                  value={p.values.amount ?? ''}
                  onChange={(val) => p.onChange({ ...p.values, amount: val })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xl font-black text-center dark:text-[#f0f6fc] font-mono transition-all"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('pf.note_optional')}</label>
                <input
                  type="text"
                  value={p.values.note || ''}
                  onChange={(e) => p.onChange({ ...p.values, note: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-bold dark:text-[#f0f6fc]"
                  placeholder={t('pf.details_ph')}
                />
              </div>
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl text-xs font-bold text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/30">{t('pf.cap_explain')}</div>
              <button
                type="submit"
                disabled={p.busy}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-emerald-600/20 transition-all cursor-pointer"
                data-testid="capitalize-submit"
              >
                {t('pf.cap_submit')}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export function WithdrawProfitModal(p: SettlementModalProps) {
  const { t } = useI18n();
  const options = [
    { id: 'IQD', label: t('pf.iqd_long'), subLabel: `${t('pf.available')}: ${p.pending.IQD} د.ع` },
    ...(p.pending.USD !== null ? [{ id: 'USD', label: t('pf.usd_long'), subLabel: `${t('pf.available')}: $${p.pending.USD}` }] : []),
  ];
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="withdraw-profit-modal">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={p.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border"
          >
            <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-blue-900 text-white">
              <h3 className="text-xl font-black flex items-center gap-2 uppercase tracking-tight">
                <TrendingDown className="text-blue-400" />
                {t('pf.wd_title')}
              </h3>
              <button onClick={p.onClose} className="text-blue-400 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={submit(p.onSubmit)} className="p-6 space-y-4">
              <div>
                <CustomSelect
                  label={t('pf.select_currency')}
                  options={options}
                  value={p.values.currency || 'IQD'}
                  onChange={(val) => p.onChange({ ...p.values, currency: val as 'IQD' | 'USD' })}
                  headerTitle={t('pf.wd_currencies')}
                  searchable={true}
                  size="md"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('pf.wd_amount', { cur: p.values.currency || 'IQD' })}</label>
                <NumericInput
                  required
                  value={p.values.amount ?? ''}
                  onChange={(val) => p.onChange({ ...p.values, amount: val })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-xl font-black text-center dark:text-[#f0f6fc] font-mono transition-all"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('pf.note_optional')}</label>
                <input
                  type="text"
                  value={p.values.note || ''}
                  onChange={(e) => p.onChange({ ...p.values, note: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-xs font-bold dark:text-[#f0f6fc]"
                  placeholder={t('pf.details_ph')}
                />
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-2xl text-xs font-bold text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-900/30">{t('pf.wd_explain')}</div>
              <button
                type="submit"
                disabled={p.busy}
                className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-blue-600/20 transition-all cursor-pointer"
                data-testid="withdraw-profit-submit"
              >
                {t('pf.wd_submit')}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export interface ConvertProfitValues {
  readonly amount: string;
  /** D-012 quote, pre-filled with the approved USD buy quote (D-101). */
  readonly rate: string;
  readonly note: string;
}

export interface ConvertProfitModalProps {
  readonly open: boolean;
  readonly values: ConvertProfitValues;
  readonly onChange: (v: ConvertProfitValues) => void;
  /** Pending USD profit label ("100"). */
  readonly availableUsd: string;
  /** The server preview's IQD figure ("152,500"), or null while there is none. */
  readonly equivalent: string | null;
  readonly busy: boolean;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
}

export function ConvertProfitModal(p: ConvertProfitModalProps) {
  const { t } = useI18n();
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="convert-modal">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={p.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border"
          >
            <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-emerald-900 text-white">
              <h3 className="text-xl font-black flex items-center gap-2 uppercase tracking-tight">
                <RefreshCw className="text-emerald-400" />
                {t('pf.conv_title')}
              </h3>
              <button onClick={p.onClose} className="text-emerald-400 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={submit(p.onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('pf.conv_source')}</label>
                <select
                  value="USD"
                  onChange={() => undefined}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-sm font-black dark:text-[#f0f6fc] transition-all"
                >
                  <option value="USD">{t('pf.opt_usd_conv_available', { amount: p.availableUsd })}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('pf.conv_amount', { cur: 'USD' })}</label>
                <NumericInput
                  required
                  value={p.values.amount}
                  onChange={(val) => p.onChange({ ...p.values, amount: val })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xl font-black text-center dark:text-[#f0f6fc] font-mono transition-all"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('pf.conv_rate')}</label>
                <NumericInput
                  required
                  value={p.values.rate}
                  onChange={(val) => p.onChange({ ...p.values, rate: val })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xl font-black text-center dark:text-[#f0f6fc] font-mono transition-all"
                  placeholder="152.5"
                />
              </div>
              {p.equivalent !== null && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-950/20 rounded-2xl border border-emerald-200 dark:border-emerald-800/40 text-center space-y-1" data-testid="convert-equivalent">
                  <span className="text-xs text-emerald-700 dark:text-emerald-400 font-bold block">{t('pf.conv_equivalent')}</span>
                  <span className="text-2xl font-black font-mono text-emerald-600 dark:text-emerald-400">{p.equivalent} د.ع</span>
                </div>
              )}
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('pf.note_optional')}</label>
                <input
                  type="text"
                  value={p.values.note}
                  onChange={(e) => p.onChange({ ...p.values, note: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-bold dark:text-[#f0f6fc]"
                  placeholder={t('pf.details_ph')}
                />
              </div>
              <button
                type="submit"
                disabled={p.busy}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-emerald-600/20 transition-all cursor-pointer"
                data-testid="convert-submit"
              >
                {t('pf.conv_submit')}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export type ExpenseCategoryKey = 'salary' | 'tip' | 'outlet' | 'withdrawal_comm' | 'other';

export interface AddExpenseValues {
  readonly category: ExpenseCategoryKey;
  readonly amount: string;
  readonly note: string;
  /** Salary: the employee's user id. */
  readonly employeeId: string;
}

export interface AddExpenseModalProps {
  readonly open: boolean;
  readonly values: AddExpenseValues;
  readonly onChange: (v: AddExpenseValues) => void;
  readonly employees: ReadonlyArray<{ readonly id: string; readonly username: string; readonly manager: boolean }>;
  readonly busy: boolean;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
}

export function AddExpenseModal(p: AddExpenseModalProps) {
  const { t } = useI18n();
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="add-expense-modal">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={p.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative z-10 border border-gray-100 dark:border-[#30363d] dark:digital-border"
          >
            <div className="p-6 border-b border-gray-50 dark:border-[#30363d] flex justify-between items-center bg-gray-50/50 dark:bg-[#161b22]/50">
              <h3 className="text-xl font-black text-gray-900 dark:text-[#f0f6fc] uppercase tracking-tight dark:digital-glow">{t('addExpense')}</h3>
              <button onClick={p.onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={submit(p.onSubmit)} className="p-6 space-y-4">
              <div>
                <CustomSelect
                  label={t('expenseCategory')}
                  options={[
                    { id: 'salary', label: t('salary') },
                    { id: 'tip', label: t('tip') },
                    { id: 'outlet', label: t('outlet') },
                    { id: 'withdrawal_comm', label: t('withdrawal_comm') },
                    { id: 'other', label: t('other') },
                  ]}
                  value={p.values.category || 'salary'}
                  onChange={(val) => p.onChange({ ...p.values, category: val as ExpenseCategoryKey })}
                  headerTitle={t('pf.exp_categories')}
                  searchable={false}
                  size="md"
                />
              </div>
              {p.values.category === 'salary' && (
                <div>
                  <CustomSelect
                    label={t('pf.employee')}
                    options={[
                      { id: '', label: t('pf.select_employee_long') },
                      ...p.employees.map((u) => ({ id: u.id, label: u.username, badge: u.manager ? t('pf.badge_admin') : t('pf.badge_staff') })),
                    ]}
                    value={p.values.employeeId || ''}
                    onChange={(val) => p.onChange({ ...p.values, employeeId: String(val) })}
                    headerTitle={t('pf.employees_list')}
                    placeholder={t('pf.select_employee')}
                    searchable={true}
                    size="md"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('expenseAmount')}</label>
                <NumericInput
                  required
                  value={p.values.amount ?? ''}
                  onChange={(val) => p.onChange({ ...p.values, amount: val })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-mono text-lg dark:text-[#f0f6fc] transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('expenseNote')}</label>
                <textarea
                  value={p.values.note || ''}
                  onChange={(e) => p.onChange({ ...p.values, note: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-bold dark:text-[#f0f6fc] transition-all h-24"
                  data-testid="add-expense-note"
                />
              </div>
              <button
                type="submit"
                disabled={p.busy}
                className="w-full py-4 bg-rose-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20 flex items-center justify-center gap-2"
                data-testid="add-expense-submit"
              >
                <Plus size={20} />
                {t('confirm')}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
